import API_CONFIG from '../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';

// =============================================================================
// Tax pipeline API client (CTR-014).
//
// Wraps the five `/v1/tax/pipeline/...` endpoints exposed by
// TaxPipelineApiHandler. Mirrors the `apiFetch` pattern used in
// `src/api/tax.ts` (Cognito ID token + AbortController timeout) but binds
// to `API_CONFIG.taxPipelineBaseUrl` (separate API Gateway).
//
// All BigDecimal financials cross the wire as strings to preserve exactness;
// summary numeric counts (transactionsImported, taxLotCount, etc.) are
// JSON numbers.
// =============================================================================

// =============================================================================
// Types — mirror the response shapes produced by TaxPipelineApiHandler.
// =============================================================================

export type PipelineRunStatus =
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'TIMED_OUT'
  | 'ABORTED';

export type PipelineRunMethod = 'HIFO' | 'LIFO' | 'FIFO';

export interface TriggeredBy {
  sub: string;
  email: string;
}

export interface PipelineRunInput {
  taxYear: number;
  methods: PipelineRunMethod[];
  formats: string[];
  triggeredBy: TriggeredBy;
}

/** List item returned by `GET /v1/tax/pipeline/runs`. */
export interface PipelineRun {
  executionName: string;
  status: PipelineRunStatus;
  startedAt: string;
  finishedAt?: string | null;
  taxYear: number | 'unknown';
  methods: PipelineRunMethod[] | 'unknown';
  formats: string[] | 'unknown';
  triggeredBy: TriggeredBy | 'unknown';
}

export interface PipelineRunDataVersion {
  lastImportAt: string;
  manualLotCount: number;
  coinbaseCorrectionsCount: number;
}

/** BigDecimals serialize as strings (preserved from the backend). */
export interface PipelineRunSummary {
  transactionsImported: number;
  transactionsMatched: number;
  transactionsUnmatched: number;
  taxLotCount: number;
  totalProceeds: string;
  totalCostBasis: string;
  totalGainLoss: string;
}

export interface PipelineRunFile {
  name: string;
  size: number;
  lastModified: string;
  format: string;
  method: string;
  s3Key: string;
}

export interface PipelineRunFailure {
  message: string;
  failedStep: string;
  recommendedAction: string;
}

/**
 * Per-method block on the describe response. When the consolidated manifest
 * exists, this is keyed by method name with summary + files. For legacy
 * runs (no manifest), the backend may surface a bare string array of method
 * names — modelled here as `string[]` for graceful degradation.
 */
export type PipelineRunDetailMethods =
  | { [method: string]: { summary: PipelineRunSummary } }
  | string[];

export interface PipelineRunDetail extends Omit<PipelineRun, 'methods'> {
  dataVersion?: PipelineRunDataVersion;
  /**
   * Per-method block keyed by method name (HIFO/LIFO/FIFO) when the
   * consolidated manifest is present, or a string array of method names
   * for legacy runs without a manifest. Null/undefined when the run is
   * still in progress and the manifest hasn't been written yet.
   */
  methods: PipelineRunDetailMethods | null | undefined;
  failure?: PipelineRunFailure;
}

/**
 * Live status summary returned by `GET /v1/tax/pipeline/runs/{name}/status`.
 * Polled every 5s while a run is non-terminal; serves the Live Progress block.
 */
export interface PipelineRunStatusSummary {
  status: PipelineRunStatus;
  finishedAt?: string | null;
  /** Current SFN state name (live progress for in-flight runs). Null when unknown or run is terminal. */
  currentStateName?: string | null;
  /** Timestamp of most recent SFN history event. */
  lastEventAt?: string | null;
  /** Type of most recent SFN history event (e.g., "TaskSucceeded", "MapIterationStarted"). */
  lastEventType?: string | null;
}

// Request/response shapes for the action endpoints.

export interface StartPipelineRunInput {
  taxYear: number;
  methods: PipelineRunMethod[];
  formats: string[];
}

export interface StartPipelineRunResponse {
  executionName: string;
  executionArn: string;
}

export interface ListPipelineRunsFilter {
  taxYear?: number;
  status?: PipelineRunStatus;
  nextToken?: string;
}

export interface ListPipelineRunsResponse {
  items: PipelineRun[];
  nextToken?: string;
}

export interface PresignPipelineRunFileResponse {
  url: string;
  expiresAt: string;
}

// =============================================================================
// Auth + fetch primitives — mirror src/api/tax.ts but bind to taxPipelineBaseUrl.
// =============================================================================

async function getAuthToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return undefined;
  }
}

async function apiFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const baseUrl = API_CONFIG.taxPipelineBaseUrl;
  const url = `${baseUrl}${endpoint}`;

  if (baseUrl === 'http://localhost:3000') {
    throw new Error(
      'Tax Pipeline API endpoint not configured. Set VITE_TAX_PIPELINE_API_BASE_URL in your environment.'
    );
  }

  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
        ...options?.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Surface the body when the server returns structured error JSON
      // (e.g., 409 with `conflictingExecutionName`) so callers can inspect.
      let bodyText: string | undefined;
      try {
        bodyText = await response.text();
      } catch {
        bodyText = undefined;
      }
      const err = new Error(
        `Tax Pipeline API Error: ${response.status} - ${response.statusText}${
          bodyText ? ` - ${bodyText}` : ''
        }`
      ) as Error & { status?: number; body?: string };
      err.status = response.status;
      err.body = bodyText;
      throw err;
    }

    return (await response.json()) as T;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') throw new Error('Request timeout - please try again');
      throw error;
    }
    throw new Error('Unknown error occurred');
  }
}

function buildQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// =============================================================================
// Endpoint functions
// =============================================================================

/** POST /v1/tax/pipeline/runs */
export async function startPipelineRun(
  input: StartPipelineRunInput,
  signal?: AbortSignal
): Promise<StartPipelineRunResponse> {
  // Backend wraps in singleEnvelope: { data: { executionName, executionArn }, metadata }.
  const response = await apiFetch<{
    data: StartPipelineRunResponse;
    metadata: unknown;
  }>('/v1/tax/pipeline/runs', {
    method: 'POST',
    body: JSON.stringify(input),
    signal,
  });
  return response.data;
}

/** GET /v1/tax/pipeline/runs */
export async function listPipelineRuns(
  filter: ListPipelineRunsFilter = {},
  signal?: AbortSignal
): Promise<ListPipelineRunsResponse> {
  return apiFetch<ListPipelineRunsResponse>(
    `/v1/tax/pipeline/runs${buildQuery(filter as Record<string, unknown>)}`,
    { signal }
  );
}

/** GET /v1/tax/pipeline/runs/{executionName} */
export async function describePipelineRun(
  executionName: string,
  signal?: AbortSignal
): Promise<PipelineRunDetail> {
  // Backend wraps in singleEnvelope: { data, metadata }. Unwrap to keep
  // PipelineRunDetail callers working with the bare detail shape.
  const response = await apiFetch<{ data: PipelineRunDetail; metadata: unknown }>(
    `/v1/tax/pipeline/runs/${encodeURIComponent(executionName)}`,
    { signal }
  );
  return response.data;
}

/** GET /v1/tax/pipeline/runs/{executionName}/status */
export async function getPipelineRunStatus(
  executionName: string,
  signal?: AbortSignal
): Promise<PipelineRunStatusSummary> {
  // Backend wraps in singleEnvelope: { data, metadata }.
  const response = await apiFetch<{
    data: PipelineRunStatusSummary;
    metadata: unknown;
  }>(`/v1/tax/pipeline/runs/${encodeURIComponent(executionName)}/status`, {
    signal,
  });
  return response.data;
}

/** GET /v1/tax/pipeline/runs/{executionName}/files */
export async function listPipelineRunFiles(
  executionName: string,
  signal?: AbortSignal
): Promise<PipelineRunFile[]> {
  const response = await apiFetch<{ items: PipelineRunFile[]; metadata: unknown }>(
    `/v1/tax/pipeline/runs/${encodeURIComponent(executionName)}/files`,
    { signal }
  );
  return response.items;
}

// Mirrors EXEC_NAME_LEGACY in crypto-trading-api/src/main/java/com/svjubilee/trading/crypto/coinbase/api/lambda/TaxPipelineApiHandler.java:74-75
export const EXECUTION_NAME_PATTERN = /^tax-\d{4}-\w+$/;

export function isConformingExecutionName(name: string): boolean {
  return EXECUTION_NAME_PATTERN.test(name);
}

/** POST /v1/tax/pipeline/runs/{executionName}/files/presign */
export async function presignPipelineRunFile(
  executionName: string,
  s3Key: string,
  signal?: AbortSignal
): Promise<PresignPipelineRunFileResponse> {
  // Backend wraps in singleEnvelope: { data: { url, expiresAt }, metadata }.
  const response = await apiFetch<{
    data: PresignPipelineRunFileResponse;
    metadata: unknown;
  }>(
    `/v1/tax/pipeline/runs/${encodeURIComponent(executionName)}/files/presign`,
    {
      method: 'POST',
      body: JSON.stringify({ s3Key }),
      signal,
    }
  );
  return response.data;
}
